import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, RefreshCcw, Trash2, Inbox, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient, http, parseApiError } from "@/app/services/api";
import { formatGroupedNumber } from "@/app/utils/format";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label, RequiredLabel } from "@/app/components/ui/label";
import { Select } from "@/app/components/ui/select";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/components/ui/card";
import { Badge } from "@/app/components/ui/badge";
import { LoadingState, EmptyState, ErrorState } from "@/app/components/ui/empty-state";
import { Table, TBody, TD, TH, THead, TR } from "@/app/components/ui/table";
import {
  DataPagination,
  DEFAULT_PAGE_SIZE,
  usePagedItems,
} from "@/app/components/ui/data-pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import type { Plan, PlanAppItem, AppState } from "@/app/types/api";

type BillingInterval = "month" | "semiannual" | "year";

export function PlansPage() {
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editorCode, setEditorCode] = React.useState<string | null>(null);

  return (
    <div className="space-y-4">
      <PlansListCard
        onCreate={() => setCreateOpen(true)}
        onEdit={(code) => setEditorCode(code)}
      />
      <PlanCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <PlanEditorDialog code={editorCode} onClose={() => setEditorCode(null)} />
    </div>
  );
}

function PlansListCard({
  onCreate,
  onEdit,
}: {
  onCreate: () => void;
  onEdit: (code: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [pageSize, setPageSize] = React.useState<number>(DEFAULT_PAGE_SIZE);
  const list = useQuery({
    queryKey: ["admin", "plans"],
    queryFn: () => http.get<{ items: Plan[] }>("/api/v1/admin/plans"),
  });

  const items = list.data?.items ?? [];
  const { page, setPage, totalPages, pageItems } = usePagedItems(items, pageSize);
  React.useEffect(() => {
    setPage(1);
  }, [pageSize, setPage]);
  const lang = i18n.resolvedLanguage ?? i18n.language ?? "vi";
  const changeLabel =
    lang === "ja" ? "変更" : lang === "en" ? "Change" : "Thay đổi";

  return (
    <Card className="gap-4">
      <CardHeader className="border-b border-border px-5 pt-5 [.border-b]:pb-4">
        <CardTitle>{t("view.plans.title")}</CardTitle>
        <CardDescription>{t("view.plans.sub")}</CardDescription>
        <CardAction className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            <RefreshCcw className="h-3.5 w-3.5" />
            {t("plans.reload")}
          </Button>
          <Button size="sm" onClick={onCreate}>
            <Plus className="h-3.5 w-3.5" />
            {t("plans.new")}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="px-5 pb-5 pt-4">
        {list.isLoading ? (
          <LoadingState />
        ) : list.isError ? (
          <ErrorState message={t("plans.empty")} onRetry={() => list.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState icon={Inbox} message={t("plans.empty")} />
        ) : (
          <div className="flex flex-col gap-2">
            <Table>
              <THead>
                <TR>
                  <TH className="w-14">#</TH>
                  <TH>{t("plans.col.code")} / {t("plans.col.name")}</TH>
                  <TH>{t("plans.col.price")}</TH>
                  <TH>{t("plans.col.apps")}</TH>
                  <TH>{t("plans.col.interval")}</TH>
                  <TH>{t("plans.col.grace")}</TH>
                  <TH></TH>
                </TR>
              </THead>
              <TBody>
                {pageItems.map((p, index) => (
                  <TR key={p.code}>
                    <TD className="text-xs text-muted-foreground">
                      {(page - 1) * pageSize + index + 1}
                    </TD>
                    <TD>
                      <div className="space-y-0.5">
                        <div className="font-mono text-xs text-foreground">{p.code}</div>
                        <div className="text-sm text-muted-foreground">{p.name || "—"}</div>
                      </div>
                    </TD>
                    <TD className="text-xs">
                      {p.price_amount != null
                        ? `${formatGroupedNumber(p.price_amount)} ${p.currency}`
                        : "—"}
                    </TD>
                    <TD>{p.app_count || 0}</TD>
                    <TD className="text-xs">{t(`plans.interval.${p.billing_interval}` as "plans.interval.month")}</TD>
                    <TD>{p.grace_period_days}</TD>
                    <TD>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => onEdit(p.code)}
                      >
                        {changeLabel}
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <DataPagination
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              onChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PlanFields({
  code,
  name,
  price,
  currency,
  interval,
  grace,
  onCodeChange,
  onNameChange,
  onPriceChange,
  onCurrencyChange,
  onIntervalChange,
  onGraceChange,
  disableCode,
}: {
  code: string;
  name: string;
  price: string;
  currency: string;
  interval: BillingInterval;
  grace: number;
  onCodeChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onPriceChange: (value: string) => void;
  onCurrencyChange: (value: string) => void;
  onIntervalChange: (value: BillingInterval) => void;
  onGraceChange: (value: number) => void;
  disableCode?: boolean;
}) {
  const { t } = useTranslation();
  const fieldIdPrefix = disableCode ? "plan-editor" : "plan-create";

  return (
    <div className="grid grid-cols-1 gap-2.5 py-2">
      <div className="flex flex-col gap-1.5">
        <RequiredLabel
          htmlFor={`${fieldIdPrefix}-code`}
          className="text-xs font-medium text-muted-foreground"
        >
          {t("plans.field.code")}
        </RequiredLabel>
        <Input
          id={`${fieldIdPrefix}-code`}
          aria-label={t("plans.field.code")}
          value={code}
          onChange={(e) => onCodeChange(e.target.value)}
          placeholder="vd: pro-yearly"
          disabled={disableCode}
          autoFocus
          required
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor={`${fieldIdPrefix}-name`}
          className="text-xs font-medium text-muted-foreground"
        >
          {t("plans.field.name")}
        </Label>
        <Input
          id={`${fieldIdPrefix}-name`}
          aria-label={t("plans.field.name")}
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
        />
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <div className="flex flex-col gap-1.5">
          <RequiredLabel
            htmlFor={`${fieldIdPrefix}-price`}
            className="text-xs font-medium text-muted-foreground"
          >
            {t("plans.field.price")}
          </RequiredLabel>
          <Input
            id={`${fieldIdPrefix}-price`}
            aria-label={t("plans.field.price")}
            type="number"
            min={0}
            step="0.01"
            value={price}
            onChange={(e) => onPriceChange(e.target.value)}
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <RequiredLabel
            htmlFor={`${fieldIdPrefix}-currency`}
            className="text-xs font-medium text-muted-foreground"
          >
            {t("plans.field.currency")}
          </RequiredLabel>
          <Input
            id={`${fieldIdPrefix}-currency`}
            aria-label={t("plans.field.currency")}
            value={currency}
            onChange={(e) => onCurrencyChange(e.target.value)}
            required
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <div className="flex flex-col gap-1.5">
          <RequiredLabel
            htmlFor={`${fieldIdPrefix}-interval`}
            className="text-xs font-medium text-muted-foreground"
          >
            {t("plans.field.interval")}
          </RequiredLabel>
          <Select
            id={`${fieldIdPrefix}-interval`}
            aria-label={t("plans.field.interval")}
            value={interval}
            onChange={(e) => onIntervalChange(e.target.value as BillingInterval)}
            required
          >
            <option value="month">{t("plans.interval.month")}</option>
            <option value="semiannual">{t("plans.interval.semiannual")}</option>
            <option value="year">{t("plans.interval.year")}</option>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <RequiredLabel
            htmlFor={`${fieldIdPrefix}-grace`}
            className="text-xs font-medium text-muted-foreground"
          >
            {t("plans.field.grace")}
          </RequiredLabel>
          <Input
            id={`${fieldIdPrefix}-grace`}
            aria-label={t("plans.field.grace")}
            type="number"
            min={0}
            value={grace}
            onChange={(e) => onGraceChange(Number(e.target.value) || 0)}
            required
          />
        </div>
      </div>
    </div>
  );
}

function PlanCreateDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [code, setCode] = React.useState("");
  const [name, setName] = React.useState("");
  const [price, setPrice] = React.useState("");
  const [currency, setCurrency] = React.useState("VND");
  const [interval, setInterval] = React.useState<BillingInterval>("month");
  const [grace, setGrace] = React.useState(3);

  React.useEffect(() => {
    if (!open) return;
    setCode("");
    setName("");
    setPrice("");
    setCurrency("VND");
    setInterval("month");
    setGrace(3);
  }, [open]);

  const submit = useMutation({
    mutationFn: async () => {
      if (!code.trim()) throw new Error(t("plans.needCode"));
      return apiClient.post<Plan>("/api/v1/admin/plans", {
        code,
        name: name || null,
        price_amount: price ? Number(price) : null,
        currency: currency || "VND",
        billing_interval: interval,
        grace_period_days: Number(grace) || 3,
      });
    },
    onSuccess: (res) => {
      const c = (res.data as Plan).code;
      toast.success(t("plans.created", { code: c }));
      qc.invalidateQueries({ queryKey: ["admin", "plans"] });
      onClose();
    },
    onError: (e) => {
      toast.error(parseApiError(e));
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("plans.form.createTitle")}</DialogTitle>
          <DialogDescription>{t("plans.form.createDescription")}</DialogDescription>
        </DialogHeader>
        <PlanFields
          code={code}
          name={name}
          price={price}
          currency={currency}
          interval={interval}
          grace={grace}
          onCodeChange={setCode}
          onNameChange={setName}
          onPriceChange={setPrice}
          onCurrencyChange={setCurrency}
          onIntervalChange={setInterval}
          onGraceChange={setGrace}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submit.isPending}>
            {t("dialog.common.cancel")}
          </Button>
          <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
            {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("plans.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlanEditorDialog({
  code,
  onClose,
}: {
  code: string | null;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = React.useState<"details" | "apps">("details");
  const [name, setName] = React.useState("");
  const [price, setPrice] = React.useState("");
  const [currency, setCurrency] = React.useState("VND");
  const [interval, setInterval] = React.useState<BillingInterval>("month");
  const [grace, setGrace] = React.useState(3);
  const [apps, setApps] = React.useState<PlanAppItem[]>([]);
  const [allApps, setAllApps] = React.useState<{ app_id: string; name: string }[]>([]);
  const [selectedApp, setSelectedApp] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const list = useQuery({
    queryKey: ["admin", "plans"],
    queryFn: () => http.get<{ items: Plan[] }>("/api/v1/admin/plans"),
  });

  React.useEffect(() => {
    if (!code) return;
    setActiveTab("details");
  }, [code]);

  React.useEffect(() => {
    if (!code) return;
    const plan = (list.data?.items ?? []).find((x) => x.code === code);
    if (plan) {
      setName(plan.name || "");
      setPrice(plan.price_amount != null ? String(plan.price_amount) : "");
      setCurrency(plan.currency);
      setInterval(plan.billing_interval);
      setGrace(plan.grace_period_days);
    }
  }, [code, list.data]);

  React.useEffect(() => {
    if (!code) return;
    setSelectedApp("");
    setLoading(true);
    Promise.all([
      http.get<{ items: PlanAppItem[] }>(
        `/api/v1/admin/plans/${encodeURIComponent(code)}/apps`
      ),
      http.get<{ items: { app_id: string; name: string }[] }>(
        "/api/v1/admin/apps",
        { limit: 500 }
      ),
    ])
      .then(([appsRes, allRes]) => {
        setApps(appsRes.items || []);
        setAllApps(allRes.items || []);
      })
      .finally(() => setLoading(false));
  }, [code]);

  const submit = useMutation({
    mutationFn: async () => {
      if (!code) throw new Error("cancelled");
      return apiClient.patch<Plan>(
        `/api/v1/admin/plans/${encodeURIComponent(code)}`,
        {
          name: name || null,
          price_amount: price ? Number(price) : null,
          currency: currency || "VND",
          billing_interval: interval,
          grace_period_days: Number(grace) || 3,
        }
      );
    },
    onSuccess: (res) => {
      const nextCode = (res.data as Plan).code;
      toast.success(t("plans.updated", { code: nextCode }));
      qc.invalidateQueries({ queryKey: ["admin", "plans"] });
      onClose();
    },
    onError: (e) => {
      if (e instanceof Error && e.message === "cancelled") return;
      toast.error(parseApiError(e));
    },
  });

  const add = useMutation({
    mutationFn: async () => {
      if (!code) throw new Error("cancelled");
      if (!selectedApp) throw new Error("cancelled");
      await apiClient.post(
        `/api/v1/admin/plans/${encodeURIComponent(code)}/apps`,
        { app_id: selectedApp }
      );
      return http.get<{ items: PlanAppItem[] }>(
        `/api/v1/admin/plans/${encodeURIComponent(code)}/apps`
      );
    },
    onSuccess: (res) => {
      if (!code) return;
      toast.success(t("plans.apps.added", { app: selectedApp, code }));
      setApps(res.items || []);
      setSelectedApp("");
      qc.invalidateQueries({ queryKey: ["admin", "plans"] });
    },
    onError: (e) => {
      if (e instanceof Error && e.message === "cancelled") return;
      toast.error(parseApiError(e));
    },
  });

  const remove = useMutation({
    mutationFn: async (appId: string) => {
      if (!code) throw new Error("cancelled");
      await apiClient.delete(
        `/api/v1/admin/plans/${encodeURIComponent(code)}/apps/${encodeURIComponent(appId)}`
      );
      return appId;
    },
    onSuccess: (appId) => {
      if (!code) return;
      toast.success(t("plans.apps.removed", { app: appId, code }));
      setApps((prev) => prev.filter((a) => a.app_id !== appId));
      qc.invalidateQueries({ queryKey: ["admin", "plans"] });
    },
    onError: (e) => {
      if (e instanceof Error && e.message === "cancelled") return;
      toast.error(parseApiError(e));
    },
  });

  const available = allApps.filter(
    (a) => !apps.some((x) => x.app_id === a.app_id)
  );
  const lang = i18n.resolvedLanguage ?? i18n.language ?? "vi";
  const detailsTabLabel =
    lang === "ja" ? "情報" : lang === "en" ? "Details" : "Thông tin";
  const appsTabLabel =
    lang === "ja" ? "プラグイン" : lang === "en" ? "Plugins" : "Plugin";

  return (
    <Dialog open={code !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{code ? t("plans.form.editTitle", { code }) : t("plans.edit")}</DialogTitle>
          <DialogDescription>
            {code ? t("plans.form.editDescription", { code }) : t("plans.apps.help")}
          </DialogDescription>
        </DialogHeader>
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
          <TabsList>
            <TabsTrigger value="details">{detailsTabLabel}</TabsTrigger>
            <TabsTrigger value="apps">{appsTabLabel}</TabsTrigger>
          </TabsList>
          <TabsContent value="details" className="pt-2">
            <PlanFields
              code={code ?? ""}
              name={name}
              price={price}
              currency={currency}
              interval={interval}
              grace={grace}
              onCodeChange={() => undefined}
              onNameChange={setName}
              onPriceChange={setPrice}
              onCurrencyChange={setCurrency}
              onIntervalChange={setInterval}
              onGraceChange={setGrace}
              disableCode
            />
          </TabsContent>
          <TabsContent value="apps" className="pt-2">
            {loading ? (
              <LoadingState />
            ) : (
              <div className="space-y-3">
                <div className="flex flex-col gap-1.5">
                  <RequiredLabel htmlFor="plan-app-select" className="text-xs font-medium text-muted-foreground">
                    {t("plans.apps.col.app")}
                  </RequiredLabel>
                  <div className="flex gap-2">
                    <Select
                      id="plan-app-select"
                      value={selectedApp}
                      onChange={(e) => setSelectedApp(e.target.value)}
                      className="flex-1"
                      required
                    >
                      <option value="">
                        {available.length === 0 ? t("plans.apps.noAvailable") : "—"}
                      </option>
                      {available.map((a) => (
                        <option key={a.app_id} value={a.app_id}>
                          {a.name} ({a.app_id})
                        </option>
                      ))}
                    </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => add.mutate()}
                      disabled={!selectedApp || add.isPending}
                    >
                      {t("plans.apps.addBtn")}
                    </Button>
                  </div>
                </div>
                {apps.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("plans.apps.empty")}</p>
                ) : (
                  <Table>
                    <THead>
                      <TR>
                        <TH>{t("plans.apps.col.app")}</TH>
                        <TH>{t("plans.apps.col.version")}</TH>
                        <TH>{t("plans.apps.col.status")}</TH>
                        <TH></TH>
                      </TR>
                    </THead>
                    <TBody>
                      {apps.map((a) => (
                        <TR key={a.app_id}>
                          <TD>
                            <div className="font-medium">{a.name || a.app_id}</div>
                            <div className="text-xs text-muted-foreground font-mono">{a.app_id}</div>
                          </TD>
                          <TD className="text-xs">{a.latest_version || "—"}</TD>
                          <TD>
                            <PlanStateBadge state={a.state} />
                            {a.plan_type === "premium" && (
                              <Badge variant="primary" className="ml-1">
                                {t("plan.premium")}
                              </Badge>
                            )}
                          </TD>
                          <TD>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => remove.mutate(a.app_id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              {t("plans.apps.remove")}
                            </Button>
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("dialog.common.cancel")}
          </Button>
          <Button onClick={() => submit.mutate()} disabled={submit.isPending || activeTab !== "details"}>
            {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("plans.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlanStateBadge({ state }: { state: AppState }) {
  const { t } = useTranslation();
  const v: "success" | "warning" | "default" =
    state === "live" ? "success" : state === "draft" ? "warning" : "default";
  const key = state === "live" ? "state.live" : state === "draft" ? "state.draft" : "state.unavailable";
  return <Badge variant={v}>{t(key)}</Badge>;
}
